<template>
  <v-row class="ma-0" dense v-if="localValue">
    <v-col :cols="productableOptions.length ? 4 : 9" class="py-0">
      <v-autocomplete
        v-model="localValue.item_id"
        :items="productOptions"
        :loading="loadingProducts"
        item-title="label"
        item-value="value"
        label="Producto"
        density="compact"
        @update:modelValue="onProductSelect"
        @update:search="onSearch"
      />
    </v-col>

    <v-col :cols="5" v-if="productableOptions.length" class="py-0">
      <v-autocomplete
        v-model="localValue.productable_id"
        :items="productableOptions"
        item-title="label"
        item-value="value"
        label="Seleccionar"
        density="compact"
        @update:modelValue="onProductableSelect"
      />
    </v-col>

    <v-col :cols="3" class="py-0">
      <v-text-field
        v-model="localValue.unit_price"
        prefix="$"
        type="number"
        label="Precio"
        density="compact"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useProductsStore } from '@/store/products';
import { useClientsStore } from '@/store/clients';
import { debounce } from 'lodash';

const cachedSearches = new Map<string, any[]>();

const emit = defineEmits(['update:modelValue', 'validation']);
const props = defineProps({
  modelValue: { type: Object, required: true },
  clientId: { type: [String, Number], required: false },
  subjectId: { type: [String, Number], required: false }
});

const clientStore = useClientsStore();
const productsStore = useProductsStore();

const localValue = ref({ ...props.modelValue });
const productOptions = ref<any[]>([]);
const productableOptions = ref<any[]>([]);
const loadingProducts = ref(false);

const validate = () => {
  const valid =
    !!localValue.value.item_id &&
    (!!localValue.value.unit_price || localValue.value.unit_price === 0) &&
    (productableOptions.value.length === 0 || !!localValue.value.productable_id);

  emit('validation', valid);
};

watch(() => props.modelValue, (val) => {
  localValue.value = { ...val };

  if (val.productables && val.productables.length) {
    productableOptions.value = val.productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
  }
}, { deep: true });

watch(() => localValue.value, (val) => {
  emit('update:modelValue', val);
  validate();
}, { deep: true });

const onProductSelect = async (productId: number) => {
  const selected = productOptions.value.find(p => p.value === productId);
  if (!selected) return;

  localValue.value.item_id = productId;

  const productables = selected.raw?.attributes?.productable || [];

  if (productables.length > 0) {
    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
    localValue.value.productable_id = null;
    localValue.value.productables = productables;
  } else {
    await fetchPrice({
      productId,
      productType: selected.raw?.attributes?.productable_type
    });
    productableOptions.value = [];
    localValue.value.productable_id = productId;
    localValue.value.productable_type = selected.raw?.attributes?.productable_type;
  }
};

const onProductableSelect = async (productableId: number) => {
  const product = localValue.value.productables?.find((p: any) => p.id === productableId);
  if (!product) return;

  localValue.value.productable_id = productableId;
  localValue.value.productable_type = product.attributes.productable_type;

  await fetchPrice({
    productId: productableId,
    productType: product.attributes.productable_type,
  });
};

const fetchPrice = async ({
  productId,
  productType
}: {
  productId: number;
  productType: string;
}) => {
  const subjectId = props.subjectId;
  const subject = clientStore.clients_leads.find(c => c.id === subjectId);
  const clientTypeId = subject?.attributes?.client_type?.id;

  if (!subjectId || !clientTypeId || !productId || !productType) return;

  try {
    const price = await productsStore.fetchSuggestedPrice('filter[client_type_id]=' + clientTypeId + '&filter[product_type]=' + productType + '&filter[product_id]=' + productId);
    localValue.value.unit_price = price;
  } catch (err) {
    console.error('Error fetching price:', err);
  }
};

const onSearch = debounce(async (search: string) => {
  if (!search || search.length < 2) return;
  loadingProducts.value = true;

  if (cachedSearches.has(search)) {
    productOptions.value = cachedSearches.get(search)!;
    loadingProducts.value = false;
    return;
  }

  try {
    const { data } = await productsStore.fetchProducts({ 'filter[description]': search });
    const mapped = mapProducts(data);
    cachedSearches.set(search, mapped);
    productOptions.value = mapped;
  } catch (error) {
    console.error('Error searching products:', error);
  } finally {
    loadingProducts.value = false;
  }
}, 300);

const mapProducts = (data: any[]) =>
  data.map(p => ({
    value: p.id,
    label: p.attributes.description,
    raw: p
  }));

onMounted(() => {
  const { item_id, productables, productable_type, product_description } = localValue.value;

  // Si es productable y tiene product base
  if (productable_type !== 'Product' && productables?.length) {
    const product = productables[0]?.attributes?.product;

    if (product) {
      productOptions.value = [{
        value: item_id,
        label: product.attributes?.description || 'Producto',
        raw: product
      }];
    }

    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
  }

  // Si es producto directo
  if (productable_type === 'Product' && item_id) {
    productOptions.value = [{
      value: item_id,
      label: product_description,
      raw: { id: item_id }
    }];
  }
});
</script>
